interface TaskSettingsProps {
    settings: {
        task_mode: 'non_cumulative' | 'cumulative' | 'unlimited';
        allow_editing_while_in_debt: boolean;
        allow_random_assignment: boolean;
        allow_self_selection: boolean;
        penalty_on_abandon: boolean;
    };
    onChange: (settings: TaskSettingsProps['settings']) => void;
}

const MODE_OPTIONS = [
    { value: 'non_cumulative', label: 'Non-cumulative', desc: 'Only one active task instance at a time' },
    { value: 'cumulative', label: 'Cumulative', desc: 'Missed tasks stack up until completed' },
    { value: 'unlimited', label: 'Unlimited', desc: 'Tasks repeat without limit' },
] as const;

export function TaskSettings({ settings, onChange }: TaskSettingsProps) {
    const update = (key: keyof TaskSettingsProps['settings'], value: unknown) => {
        onChange({ ...settings, [key]: value });
    };

    return (
        <div className="space-y-4">
            <div>
                <label className="text-xs font-medium text-slate-400">Mode</label>
                <div className="mt-1 space-y-1">
                    {MODE_OPTIONS.map(opt => (
                        <label key={opt.value} className="flex items-start gap-2 text-sm cursor-pointer">
                            <input
                                type="radio"
                                name="task_mode"
                                value={opt.value}
                                checked={settings.task_mode === opt.value}
                                onChange={() => update('task_mode', opt.value)}
                                className="mt-0.5 rounded-full"
                            />
                            <div>
                                <span className="text-slate-200">{opt.label}</span>
                                <p className="text-[11px] text-slate-500">{opt.desc}</p>
                            </div>
                        </label>
                    ))}
                </div>
            </div>

            <div className="space-y-2">
                <label className="text-xs font-medium text-slate-400">Options</label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={settings.allow_editing_while_in_debt}
                        onChange={e => update('allow_editing_while_in_debt', e.target.checked)}
                        className="rounded"
                    />
                    <span className="text-slate-200">Allow editing tasks while in debt</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={settings.allow_random_assignment}
                        onChange={e => update('allow_random_assignment', e.target.checked)}
                        className="rounded"
                    />
                    <span className="text-slate-200">Allow assigning a task randomly or by creating a vote</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={settings.allow_self_selection}
                        onChange={e => update('allow_self_selection', e.target.checked)}
                        className="rounded"
                    />
                    <span className="text-slate-200">Allow choosing own task from the list</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={settings.penalty_on_abandon}
                        onChange={e => update('penalty_on_abandon', e.target.checked)}
                        className="rounded"
                    />
                    <span className="text-slate-200">Give a penalty when a task is abandoned</span>
                </label>
            </div>
        </div>
    );
}
